import { useState } from "react";
import { Link } from "react-router-dom";
import styles from "../styles/signInPreview.module.css";
import AuthGlassShell, { CardHead, Icon } from "./AuthGlassShell";

// Same checks as Create account; any symbol counts.
const RULES = [
  ["8+ characters", (p) => p.length >= 8],
  ["A letter and a number", (p) => /[A-Za-z]/.test(p) && /\d/.test(p)],
  ["A symbol", (p) => /[^A-Za-z0-9\s]/.test(p)],
];

const LockIcon = () => (
  <Icon>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </Icon>
);

const EyeIcon = ({ open }) =>
  open ? (
    <Icon>
      <path d="M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  ) : (
    <Icon>
      <path d="M3 3l18 18" />
      <path d="M10.6 5.1A10.4 10.4 0 0 1 12 5c6 0 9.5 7 9.5 7a17.6 17.6 0 0 1-3 3.8M6.6 6.6A17.4 17.4 0 0 0 2.5 12S6 19 12 19a9.8 9.8 0 0 0 5.4-1.6" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </Icon>
  );

// Set a new password from the link in the reset email.
export default function ResetPasswordPreview({ hasToken, onSubmit }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const valid = RULES.every(([, test]) => test(password));
  const matches = confirm !== "" && confirm === password;
  const ready = valid && matches;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    try {
      if (await onSubmit(password)) setDone(true);
    } finally {
      setBusy(false);
    }
  };

  if (!hasToken || done) {
    return (
      <AuthGlassShell>
        <div className={styles.card}>
          {done ? (
            <CardHead title="Password updated" text="You can log in with your new password now." />
          ) : (
            <CardHead title="This link doesn't work" text="Open the link from your reset email again, or ask for a new one." />
          )}
          <Link to={done ? "/signin" : "/forgot-password"} className={styles.primary}>
            {done ? "Log in" : "Send a new link"}
          </Link>
        </div>
      </AuthGlassShell>
    );
  }

  return (
    <AuthGlassShell>
      <form className={styles.card} onSubmit={submit} noValidate>
        <CardHead title="Set a new password" text="Choose a password you don't use anywhere else." />

        <label className={styles.field}>
          <span className={styles.srOnly}>New password</span>
          <LockIcon />
          <input
            type={show ? "text" : "password"}
            name="password"
            autoComplete="new-password"
            placeholder="New password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-describedby="reset-password-rules"
            autoFocus
            required
          />
          <button
            type="button"
            className={styles.reveal}
            onClick={() => setShow((v) => !v)}
            aria-label={show ? "Hide password" : "Show password"}
            aria-pressed={show}
          >
            <EyeIcon open={show} />
          </button>
        </label>

        <ul className={styles.rules} id="reset-password-rules">
          {RULES.map(([label, test]) => (
            <li key={label} className={test(password) ? styles.ok : ""}>
              {label}
            </li>
          ))}
        </ul>

        <label className={styles.field}>
          <span className={styles.srOnly}>Repeat new password</span>
          <LockIcon />
          <input
            type={show ? "text" : "password"}
            name="confirmPassword"
            autoComplete="new-password"
            placeholder="Repeat new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </label>
        {confirm !== "" && !matches && (
          <p className={styles.hint} role="status">
            The passwords don't match yet.
          </p>
        )}

        <button type="submit" className={styles.primary} disabled={!ready || busy}>
          {busy ? "Saving…" : "Save new password"}
        </button>
        <p className={styles.switch}>
          <Link to="/signin">Back to log in</Link>
        </p>
      </form>
    </AuthGlassShell>
  );
}
